// manual-e2e-test-assistant の src/shared/validation.ts と同じルールを移植
import type { Category, Scenario, TestStep } from './scenario.js'

export interface ValidationError {
  path: string
  message: string
}

export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
}

export function validateScenario(scenario: Scenario): ValidationResult {
  const errors: ValidationError[] = []

  if (!scenario.id?.trim()) {
    errors.push({ path: 'id', message: 'シナリオIDは必須です' })
  }
  if (!scenario.name?.trim()) {
    errors.push({ path: 'name', message: 'シナリオ名は必須です' })
  }
  if (!scenario.version?.trim()) {
    errors.push({ path: 'version', message: 'バージョンは必須です' })
  }

  if (!Array.isArray(scenario.categories)) {
    errors.push({ path: 'categories', message: 'カテゴリは配列である必要があります' })
    return { valid: false, errors }
  }

  const categoryIds = new Set<string>()
  const stepIds = new Set<string>()

  scenario.categories.forEach((category, catIndex) => {
    const catPath = `categories[${catIndex}]`

    if (categoryIds.has(category.id)) {
      errors.push({ path: `${catPath}.id`, message: `カテゴリID "${category.id}" が重複しています` })
    }
    categoryIds.add(category.id)

    errors.push(...validateCategory(category, catPath))

    if (!Array.isArray(category.steps)) return

    category.steps.forEach((step, stepIndex) => {
      const stepPath = `${catPath}.steps[${stepIndex}]`

      if (stepIds.has(step.id)) {
        errors.push({ path: `${stepPath}.id`, message: `ステップID "${step.id}" が重複しています` })
      }
      stepIds.add(step.id)

      errors.push(...validateStep(step, stepPath))
    })
  })

  errors.push(...checkCircularDependencies(scenario))

  return { valid: errors.length === 0, errors }
}

function validateCategory(category: Category, path: string): ValidationError[] {
  const errors: ValidationError[] = []

  if (!category.id?.trim()) {
    errors.push({ path: `${path}.id`, message: 'カテゴリIDは必須です' })
  }
  if (!category.name?.trim()) {
    errors.push({ path: `${path}.name`, message: 'カテゴリ名は必須です' })
  }
  if (!Array.isArray(category.steps)) {
    errors.push({ path: `${path}.steps`, message: 'ステップは配列である必要があります' })
  }

  return errors
}

function validateStep(step: TestStep, path: string): ValidationError[] {
  const errors: ValidationError[] = []

  if (!step.id?.trim()) {
    errors.push({ path: `${path}.id`, message: 'ステップIDは必須です' })
  }
  if (!step.title?.trim()) {
    errors.push({ path: `${path}.title`, message: 'タイトルは必須です' })
  }
  if (!step.instruction?.trim()) {
    errors.push({ path: `${path}.instruction`, message: '実行手順は必須です' })
  }
  if (!step.expected?.trim()) {
    errors.push({ path: `${path}.expected`, message: '期待結果は必須です' })
  }

  return errors
}

function checkCircularDependencies(scenario: Scenario): ValidationError[] {
  const errors: ValidationError[] = []
  const allStepIds = new Set<string>()

  scenario.categories.forEach((cat) => {
    if (!Array.isArray(cat.steps)) return
    cat.steps.forEach((step) => {
      allStepIds.add(step.id)
    })
  })

  const deps = new Map<string, string[]>()
  scenario.categories.forEach((cat, catIndex) => {
    if (!Array.isArray(cat.steps)) return
    cat.steps.forEach((step, stepIndex) => {
      if (step.dependsOn && step.dependsOn.length > 0) {
        step.dependsOn.forEach((depId) => {
          if (!allStepIds.has(depId)) {
            errors.push({
              path: `categories[${catIndex}].steps[${stepIndex}].dependsOn`,
              message: `存在しないステップ "${depId}" に依存しています`,
            })
          }
        })
        deps.set(step.id, step.dependsOn)
      }
    })
  })

  const visited = new Set<string>()
  const inStack = new Set<string>()

  function hasCycle(stepId: string, path: string[]): boolean {
    if (inStack.has(stepId)) {
      errors.push({
        path: 'dependsOn',
        message: `循環依存を検出: ${[...path, stepId].join(' -> ')}`,
      })
      return true
    }
    if (visited.has(stepId)) return false

    visited.add(stepId)
    inStack.add(stepId)

    const dependencies = deps.get(stepId) || []
    for (const depId of dependencies) {
      if (hasCycle(depId, [...path, stepId])) {
        return true
      }
    }

    inStack.delete(stepId)
    return false
  }

  for (const stepId of deps.keys()) {
    if (!visited.has(stepId)) {
      hasCycle(stepId, [])
    }
  }

  return errors
}
